import { z } from "zod";

import { APP_ROUTERS } from "@/constants/app-routers";

/**
 * The presale funnel — the four steps the launch thread specified (Slack 2026-08-26):
 * Create Account → Add Place → Pick Plan & Checkout → Presale Confirmation.
 *
 * Deliberately NOT the member funnel: no mobile OTP, no download-app screen, and no
 * onboarding. Presale buyers are sent through `/my/onboarding` on launch day instead,
 * which is the whole reason this funnel exists beside `/my` rather than inside it.
 */
export const PRESALE_STEPS = [
  { key: "account", label: "Create account", to: APP_ROUTERS.PRESALE_SIGN_UP },
  { key: "place", label: "Add your home", to: APP_ROUTERS.PRESALE_PLACE },
  {
    key: "checkout",
    label: "Reserve your spot",
    to: APP_ROUTERS.PRESALE_CHECKOUT,
  },
] as const;

export type PresaleStepKey = (typeof PRESALE_STEPS)[number]["key"];

/** 1-based position of a step, for the "Step 2 of 3" indicator. */
export function presaleStepNumber(key: PresaleStepKey): number {
  return PRESALE_STEPS.findIndex((step) => step.key === key) + 1;
}

export const PRESALE_STEP_COUNT = PRESALE_STEPS.length;

/**
 * What a campaign link carries through every step of the funnel.
 *
 * Field names match the platform DTOs they end up in (`checkoutLinkId` is the
 * `add-my-place` body field, `discountCode` the purchase-intent one) so the value
 * keeps one name from the URL to the database — no per-layer rename to get wrong.
 */
export const presaleCampaignSearchSchema = z.object({
  /** `CheckoutLink.id` of the offer being sold — the campaign link's whole payload. */
  checkoutLinkId: z.string().optional(),
  /** Campaign coupon (`loudon750`), applied to the cart and recorded on the intent. */
  discountCode: z.string().optional(),
  referId: z.string().optional(),
  referredBy: z.string().optional(),
});

export type PresaleCampaignSearch = z.infer<typeof presaleCampaignSearchSchema>;

/**
 * First-touch attribution params.
 *
 * NOT in `keepCampaignSearch` — they are captured once into analytics super props and must
 * not be dragged through every step's URL. They ARE in the two ENTRY routes' schemas
 * (`/presale` and `/presale/sign-up`), because `/presale`'s `beforeLoad` redirect forwards
 * only what its schema validated: unlisted there, a campaign link's UTM dies on the hop
 * before any component renders, and no component-level capture can recover it.
 */
export const presaleUtmSearchFragment = {
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
};

/** Step 1 → step 1b hand-off: what Clerk needs to re-send and verify the email code. */
export const presaleVerifySearchSchema = z.object({
  ...presaleCampaignSearchSchema.shape,
  email: z.string().optional(),
  /** Clerk's `emailAddressId` for the started sign-in, needed to resend the code. */
  emailAddressId: z.string().optional(),
  /** Set only for a RETURNING user, so the headline can greet them by name. */
  firstName: z.string().optional(),
});

export type PresaleVerifySearch = z.infer<typeof presaleVerifySearchSchema>;

/** Step 2 → step 3 hand-off: the intent `add-my-place` just created. */
export const presaleCheckoutSearchSchema = z.object({
  ...presaleCampaignSearchSchema.shape,
  purchaseIntentId: z.string().optional(),
});

export type PresaleCheckoutSearch = z.infer<typeof presaleCheckoutSearchSchema>;

/** Carries the campaign params forward without restating them at every navigate site. */
export function keepCampaignSearch(
  search: PresaleCampaignSearch,
): PresaleCampaignSearch {
  return {
    checkoutLinkId: search.checkoutLinkId || undefined,
    discountCode: search.discountCode || undefined,
    referId: search.referId || undefined,
    referredBy: search.referredBy || undefined,
  };
}
